import { withSortTiebreakers } from '../../../data_reports/utils';
import { getPersistedChartType } from '../../utils';
/**
 * Cube requires a filtered measure to also appear in `query.measures`, or the
 * filter is ignored. Dashboard global filters set `role: 'measure'` when they
 * are merged onto a chart. Filters without a role stay dimension filters.
 */
export const ensureFilteredMeasuresInQuery = (query, filters) => {
    const measureFilterKeys = (filters || []).filter((f) => f.role === 'measure' && f.member).map((f) => f.member);
    if (measureFilterKeys.length === 0) {
        return query;
    }
    const existing = new Set(query.measures || []);
    const missing = measureFilterKeys.filter((key) => {
        if (existing.has(key)) {
            return false;
        }
        existing.add(key);
        return true;
    });
    if (missing.length === 0) {
        return query;
    }
    return { ...query, measures: [...(query.measures || []), ...missing] };
};
export const buildQueryFromState = (state) => {
    const isTable = state.chartType === 'table';
    const persistedChartType = getPersistedChartType(state);
    const isAxisBased = [
        'bar',
        'stackedBar',
        'horizontalBar',
        'scatter',
        'line',
        'pie',
        'doughnut',
        'stackedArea',
    ].includes(persistedChartType);
    if (isTable) {
        if (!state.columns || state.columns.length === 0) {
            return null;
        }
        const dimColumns = state.columns.filter((c) => c.role === 'dimension');
        const measColumns = state.columns.filter((c) => c.role === 'measure');
        const nameToKey = new Map(state.columns.map((c) => [c.name, c.key]));
        // Apply the saved order to keys still present in columns, then append any
        // columns missing from the saved order — otherwise newly-added dimensions/
        // measures would be silently dropped from the query payload until the user
        // reorders the Order section.
        const applyOrder = (cols, savedOrder) => {
            if (!savedOrder) {
                return cols.map((c) => c.key);
            }
            const seen = new Set();
            const ordered = [];
            savedOrder.forEach((name) => {
                const key = nameToKey.get(name);
                if (key && !seen.has(key)) {
                    ordered.push(key);
                    seen.add(key);
                }
            });
            cols.forEach((c) => {
                if (!seen.has(c.key)) {
                    ordered.push(c.key);
                    seen.add(c.key);
                }
            });
            return ordered;
        };
        const orderedDimKeys = applyOrder(dimColumns, state.dimensionOrder);
        const orderedMeasKeys = applyOrder(measColumns, state.measureOrder);
        let query = { dimensions: orderedDimKeys, measures: orderedMeasKeys };
        if (state.filters?.length) {
            query.filters = state.filters
                .filter((f) => f.member && f.operator && f.values.length > 0)
                .map((f) => ({
                member: f.member,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                operator: f.operator,
                values: f.values.map(String),
            }));
            query = ensureFilteredMeasuresInQuery(query, state.filters);
        }
        const allOrderedKeys = [...orderedDimKeys, ...orderedMeasKeys];
        if (allOrderedKeys.length > 0) {
            const validSortRules = (state.defaultSort || []).filter((r) => r.key);
            if (validSortRules.length > 0) {
                const primaryOrder = validSortRules.reduce((acc, rule) => {
                    acc[rule.key] = rule.direction;
                    return acc;
                }, {});
                query.order = Object.fromEntries(withSortTiebreakers(primaryOrder, allOrderedKeys));
            }
            else {
                query.order = allOrderedKeys.reduce((acc, key) => {
                    acc[key] = 'asc';
                    return acc;
                }, {});
            }
        }
        return query;
    }
    if (isAxisBased) {
        if (!state.xAxis || !state.yAxis) {
            return null;
        }
        const requiresSplitBy = state.chartType === 'groupedBar' || state.chartType === 'stackedBar';
        if (requiresSplitBy && !state.splitBy) {
            return null;
        }
        const supportsSplitBy = state.chartType !== 'pie' && state.chartType !== 'multivariate';
        const isScatter = state.chartType === 'scatter';
        const additionalMeasureKeys = (state.additionalMeasures || []).filter((m) => m.key).map((m) => m.key);
        const dotSizeMeasureKey = isScatter && state.dotSizeMapping === 'variable' && state.dotSizeField?.key ? [state.dotSizeField.key] : [];
        // For scatter, the X-axis is a measure (FR1/FR2): it belongs under `measures`,
        // and `dimensions` reduces to splitBy (if present). For all other axis-based
        // charts the X-axis remains a dimension.
        let query = isScatter
            ? {
                dimensions: supportsSplitBy && state.splitBy ? [state.splitBy.key] : [],
                measures: [state.xAxis.key, state.yAxis.key, ...additionalMeasureKeys, ...dotSizeMeasureKey],
            }
            : {
                dimensions: [state.xAxis.key, ...(supportsSplitBy && state.splitBy ? [state.splitBy.key] : [])],
                measures: [state.yAxis.key, ...additionalMeasureKeys, ...dotSizeMeasureKey],
            };
        if (state.filters?.length) {
            query.filters = state.filters
                .filter((f) => f.member && f.operator && f.values.length > 0)
                .map((f) => ({
                member: f.member,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                operator: f.operator,
                values: f.values.map(String),
            }));
            query = ensureFilteredMeasuresInQuery(query, state.filters);
        }
        if (state.orderRules?.length) {
            query.order = state.orderRules.reduce((acc, rule) => {
                if (rule.key) {
                    acc[rule.key] = rule.direction;
                }
                return acc;
            }, {});
        }
        else {
            query.order = { [state.xAxis.key]: 'asc' };
        }
        return query;
    }
    if (state.chartType === 'number') {
        const measures = state.numberValues?.map((value) => value.value.key).filter(Boolean);
        if (!measures?.length) {
            return null;
        }
        let query = { measures };
        if (state.filters?.length) {
            query.filters = state.filters
                .filter((f) => f.member && f.operator && f.values.length > 0)
                .map((f) => ({
                member: f.member,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                operator: f.operator,
                values: f.values.map(String),
            }));
            query = ensureFilteredMeasuresInQuery(query, state.filters);
        }
        return query;
    }
    return null;
};
export const buildPivotConfigFromState = (state) => {
    const persistedChartType = getPersistedChartType(state);
    const isAxisBased = [
        'bar',
        'stackedBar',
        'horizontalBar',
        'scatter',
        'line',
        'pie',
        'doughnut',
        'stackedArea',
    ].includes(persistedChartType);
    if (!isAxisBased || !state.xAxis || !state.yAxis) {
        return undefined;
    }
    const supportsSplitBy = persistedChartType !== 'pie' && state.chartType !== 'multivariate';
    // For scatter, the X-axis is a measure, so it must not be listed under PivotConfig.x
    // (which expects dimensions). Pivot reduces to splitBy under x (when present).
    if (state.chartType === 'scatter') {
        return {
            x: supportsSplitBy && state.splitBy ? [state.splitBy.key] : [],
            y: ['measures'],
        };
    }
    return {
        x: [state.xAxis.key],
        y: supportsSplitBy && state.splitBy ? [state.splitBy.key, 'measures'] : ['measures'],
    };
};
